import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import keycode from 'keycode';
import Icon from '@dc/ui-library/src/molecules/Icon/Icon';
import Close from '@dc/ui-library/src/organisms/Close/Close';


export default class LocationSearchInput extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      inputTextValue: ''
    };

    this.onChange = this.onChange.bind(this);
    this.onClearButtonClick = this.onClearButtonClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.inputRef = React.createRef();
  }

  componentDidUpdate() {
    const { highlightedInputTextSuffix, loadingAndDisabled, disabledLabel } = this.props;
    const { inputTextValue } = this.state;

    // when loading and disabled, disabledLabel has to be in input
    if (loadingAndDisabled) {
      this.inputRef.current.value = disabledLabel;

      return;
    }

    // This fills input value with this.props.highlightedInputTextSuffix value and highlights it
    // if highlightedInputTextSuffix starts with inputTextValue it only highlights the suffix
    if (highlightedInputTextSuffix) {
      this.inputRef.current.value = this.props.highlightedInputTextSuffix;

      if (highlightedInputTextSuffix.toLowerCase().startsWith(inputTextValue.toLowerCase())) {
        this.inputRef.current.setSelectionRange(
          inputTextValue.length, this.inputRef.current.value.length, 'forward'
        );
      } else {
        this.inputRef.current.setSelectionRange(
          0, this.inputRef.current.value.length, 'forward'
        );
      }
    }
  }

  onChange(e) {
    const value = e.target.value;

    this.setState(
      () => (
        { inputTextValue: value }
      ),
      () => {
        this.props.onTextInputChange(this.state.inputTextValue);
      }
    );
  }

  onClearButtonClick(e) {
    this.setState({
      inputTextValue: ''
    });

    this.props.onClearButtonClick(e);
  }

  onKeyDown(e) {
    if (e.keyCode === keycode.codes.esc) {
      e.preventDefault();

      this.setState({
        inputTextValue: ''
      });

      this.props.onEscKeyClick(e);

    }
  }

  render() {
    const { props } = this;

    const wrapperClasses = ['dc-location-search-input-wrapper'];
    const inputClasses = ['dc-location-search-input', 'nostyle'];
    const labelClasses = ['dc-form-item-label'];

    if (props.searchInputState === 'valid') {
      inputClasses.push('dc-location-search-input-valid');
    }
    if (props.searchInputState === 'invalid') {
      inputClasses.push('dc-location-search-input-invalid');
    }
    if (props.searchInputState === 'neutral') {
      inputClasses.push('dc-location-search-input-neutral');
    }


    if (props.loadingAndDisabled) {
      inputClasses.push('dc-search-input-disabled');
    }

    if (this.state.inputTextValue !== '' || this.props.highlightedInputTextSuffix !== '') {
      labelClasses.push('dc-form-item-label-top');
    }
    return (
      <div>
        <span data-component="SearchInput" className={wrapperClasses.join(' ')}>
          <input
            aria-invalid={props.searchInputState === 'valid'}
            aria-label={props.placeholderLabel}
            autoComplete="off"
            autoCorrect="off"
            className={inputClasses.join(' ')}
            data-element="Input"
            disabled={props.loadingAndDisabled}
            id={props.name}
            name={props.name}
            onBlur={props.onTextInputBlur}
            onChange={this.onChange}
            onFocus={props.onTextInputFocus}
            onKeyDown={this.onKeyDown}
            ref={this.inputRef}
            spellCheck="false"
            tabIndex={props.tabIndex}
            type="search"
            value={props.loadingAndDisabled ? props.disabledLabel : this.state.inputTextValue}
          />

          {!props.loadingAndDisabled && this.state.inputTextValue !== '' &&
            <span className="dc-location-search-input-clear">
              <Close onClose={this.onClearButtonClick} />
            </span>
          }

          {!props.loadingAndDisabled && props.searchInputState === 'valid' &&
            <span
              className="dc-location-search-input-search-enabled"
              onClick={() => {
                props.onSearchButtonClick(this.state.inputTextValue);
              }}
              role="button"
              tabIndex="-1"
            >
              <Icon icon="Search" />
            </span>
          }

          {!props.loadingAndDisabled && props.searchInputState !== 'valid' &&
          <span className="dc-location-search-input-search-disabled">
            <Icon icon="Search" />
          </span>
          }

          {props.loadingAndDisabled &&
          <span className="dc-location-search-input-processing">
            <Icon icon="Spinner" spin />
          </span>
          }

          <label
            htmlFor={props.name}
            className={labelClasses.join(' ')}
          > {/* label Enter town postcode */}
            {props.placeholderLabel}
          </label>
        </span>
      </div>
    );
  }
}

LocationSearchInput.propTypes = {
  name: PropTypes.string.isRequired,
  placeholderLabel: PropTypes.string.isRequired,
  disabledLabel: PropTypes.string.isRequired,
  loadingAndDisabled: PropTypes.bool.isRequired,
  searchInputState: PropTypes.oneOf([
    'valid',
    'invalid',
    'neutral'
  ]).isRequired,
  highlightedInputTextSuffix: PropTypes.string,
  onTextInputChange: PropTypes.func,
  onClearButtonClick: PropTypes.func,
  onEscKeyClick: PropTypes.func,
  onSearchButtonClick: PropTypes.func,
  onTextInputBlur: PropTypes.func
};

LocationSearchInput.defaultProps = {
  highlightedInputTextSuffix: '',
  onTextInputChange: () => {},
  onClearButtonClick: () => {},
  onEscKeyClick: () => {},
  onSearchButtonClick: () => {},
  onTextInputBlur: () => {}
};
